/**
 * A party that walks a round together (10 §2, mission 29: the general and his bodyguards inspecting the
 * compound). As patrols.ts does for the garrison: long legs are broken up for the soldiers' bounded path
 * search, every node is resolved to a free cell, and each man behind the first walks the same round a pace
 * or two off it, so the file arrives everywhere together.
 */

import { atan2 } from '../../sim/math';
import type { EnemyKind, SpawnDef } from '../../sim/types';
import { NO_STAND } from '../builder';
import { FLAG } from '../grid';
import type { Point } from '../types';
import type { GoalCtx } from './types';

const MAX_LEG = 22;

export interface RoundMan {
  kind: EnemyKind;
  extra?: Partial<SpawnDef>;
}

/** Corners of a closed round, with every long leg cut into pieces. */
function subdivide(corners: readonly Point[]): Point[] {
  const out: Point[] = [];
  corners.forEach((a, i) => {
    const b = corners[(i + 1) % corners.length] ?? a;
    out.push(a);
    const length = Math.sqrt((b.x - a.x) * (b.x - a.x) + (b.z - a.z) * (b.z - a.z));
    const pieces = Math.ceil(length / MAX_LEG);
    for (let p = 1; p < pieces; p++) out.push({ x: a.x + ((b.x - a.x) * p) / pieces, z: a.z + ((b.z - a.z) * p) / pieces });
  });
  return out;
}

/** The men, on the map from the start and walking the round in file; null (attempt marked) with no room for it. */
export function escortedRound(g: GoalCtx, corners: readonly Point[], men: readonly RoundMan[]): SpawnDef[] | null {
  const round = subdivide(corners);
  const out: SpawnDef[] = [];
  for (let m = 0; m < men.length; m++) {
    const man = men[m];
    if (!man) continue;
    const nodes: Array<[number, number]> = [];
    for (const p of round) {
      // Two abreast behind the man they are guarding.
      const spot = g.map.nearestOpen(p.x + (m === 0 ? 0 : m % 2 === 0 ? -1.3 : 1.3), p.z + Math.ceil(m / 2) * 1.5, 4, NO_STAND);
      if (!spot) {
        g.map.problems.push(`${g.ctx.spec.id}: no room for the round at ${Math.round(p.x)}, ${Math.round(p.z)}`);
        return null;
      }
      nodes.push(spot);
    }
    const [a, b] = nodes;
    if (!a || !b) continue;
    out.push(g.map.spawn({ kind: man.kind, x: a[0], z: a[1], facing: atan2(b[0] - a[0], b[1] - a[1]), patrol: nodes, ...man.extra }));
    for (const [x, z] of nodes) g.map.mask.circle(x, z, 1.2, FLAG.keep);
    if (m === 0 && nodes[0]) g.map.mask.line([...nodes, nodes[0]], 1.5, FLAG.path);
  }
  return out;
}
